import {config} from '@/config/env';
import {AuthService} from '@/services/auth';

// 自定义错误类型
export class ApiError extends Error {
    status?: number;
    data?: unknown;

    constructor(message: string, status?: number, data?: unknown) {
        super(message);
        this.status = status;
        this.data = data;
        this.name = 'ApiError';
    }
}

export class TimeoutError extends Error {
    constructor(message: string) {
        super(message);
        this.name = "TimeoutError";
    }
}

// API 客户端选项接口
interface ApiClientOptions {
    method?: 'GET' | 'POST' | 'PUT' | 'PATCH' | 'DELETE';
    data?: unknown;
    headers?: Record<string, string>;
    timeout?: number;
    auth?: boolean;
    stream?: boolean;
    params?: Record<string, string | number | boolean>;
}

/**
 * API请求封装函数
 *
 * @param url - API路径（相对于BASE_URL）
 * @param options - 请求选项
 */
export const apiClient = async <T = unknown>(
    url: string,
    options: ApiClientOptions = {}
): Promise<T> => {
    // 合并默认选项
    const {
        method = 'GET',
        data = null,
        headers = {},
        timeout = 120000,
        auth = true,
        stream = false,
        params = {},
        ...otherOptions
    } = options;

    // 设置默认头
    const defaultHeaders: Record<string, string> = {
        'Accept': 'application/json',
        'Content-Type': 'application/json',
    };

    // 处理完整URL
    let fullUrl = `${config.apiBaseUrl}${url}`;

    // 添加认证token
    if (auth) {
        const token = AuthService.getAccessToken();
        if (token) {
            headers.Authorization = `${token}`;
        }
    }
    const mergedHeaders = {...defaultHeaders, ...headers};

    // 创建控制器对象用于超时处理
    const controller = new AbortController();

    // 设置超时器
    const timeoutId = setTimeout(() => {
        controller.abort();
        throw new TimeoutError(`Request timeout (${timeout}ms)`);
    }, timeout);
    if (params && Object.keys(params).length > 0) {
        const queryString = new URLSearchParams(
            Object.entries(params).map(([key, value]) => [key, String(value)])
        ).toString();
        fullUrl += (fullUrl.includes('?') ? '&' : '?') + queryString;
    }
    try {
        // 构建fetch选项
        const fetchOptions: RequestInit = {
            method,
            headers: mergedHeaders,
            signal: controller.signal,
            ...otherOptions
        };

        // 添加请求体
        if (data) {
            console.log(data)
            if (data instanceof FormData) {
                fetchOptions.body = data;
            } else if (mergedHeaders['Content-Type'] === 'application/x-www-form-urlencoded') {
                if (typeof data === 'string') {
                    fetchOptions.body = data;
                } else if (typeof data === 'object') {
                    fetchOptions.body = new URLSearchParams(data as Record<string, string>).toString();
                }
            } else {
                fetchOptions.body = JSON.stringify(data);
            }
        }

        // 执行请求
        const response = await fetch(fullUrl, fetchOptions);

        // 清除超时定时器
        clearTimeout(timeoutId);

        // 处理未授权情况
        if (response.status === 401) {
            // 尝试刷新token
            const newToken = await AuthService.refreshToken();
            if (newToken) {
                // 使用新token重试请求
                headers.Authorization = `Bearer ${newToken}`;
                return apiClient(url, {...options, headers});
            }
            throw new ApiError('Unauthorized access', 401);
        }

        // 处理其他错误状态
        if (!response.ok) {
            // 尝试解析错误信息
            let errorData: unknown;
            try {
                // 首先检查响应的Content-Type
                const contentType = response.headers.get('content-type');
                if (contentType && contentType.includes('application/json')) {
                    errorData = await response.json();
                } else {
                    // 如果不是JSON格式，尝试获取文本内容
                    const text = await response.text();
                    // 检查文本是否为JSON格式
                    try {
                        errorData = JSON.parse(text);
                    } catch {
                        errorData = {message: text || response.statusText};
                    }
                }
                // eslint-disable-next-line @typescript-eslint/no-unused-vars
            } catch (e) {
                // 如果无法解析响应体，则使用状态文本
                errorData = {message: response.statusText};
            }

            // 优先使用msg字段作为错误信息，如果没有则使用message字段
            const errorMessage = (errorData as { msg?: string; message?: string })?.msg ||
                                (errorData as { msg?: string; message?: string })?.message ||
                                `Request failed: ${response.status}`;

            throw new ApiError(
                errorMessage,
                response.status,
                errorData
            );
        }

        // 处理流式响应
        if (stream) {
            return response as T;
        }

        // 处理普通响应
        const contentType = response.headers.get('content-type');
        if (contentType && contentType.includes('application/json')) {
            return response.json() as Promise<T>;
        } else {
            // 如果响应不是JSON格式，直接返回文本
            const text = await response.text();
            try {
                return JSON.parse(text);
            } catch {
                return text as unknown as T;
            }
        }

    } catch (error) {
        clearTimeout(timeoutId);

        // 处理特定错误类型
        if (error instanceof Error && error.name === 'AbortError') {
            throw new TimeoutError(`Request interrupted (${timeout}ms)`);
        }

        if (error instanceof Error && error.name === 'TypeError') {
            // 网络错误
            error.message = 'Network connection failed, please check your network settings';
        }

        console.error('API request error:', error);
        throw error;
    }
};

/**
 * API 基础方法接口
 */
interface ApiBase {
    get<T = unknown>(url: string, options?: Omit<ApiClientOptions, 'method' | 'data'>): Promise<T>;

    post<T = unknown>(url: string, data?: unknown, options?: Omit<ApiClientOptions, 'method'>): Promise<T>;

    put<T = unknown>(url: string, data?: unknown, options?: Omit<ApiClientOptions, 'method'>): Promise<T>;

    patch<T = unknown>(url: string, data?: unknown, options?: Omit<ApiClientOptions, 'method'>): Promise<T>;

    delete<T = unknown>(url: string, options?: Omit<ApiClientOptions, 'method' | 'data'>): Promise<T>;

    stream(url: string, options?: Omit<ApiClientOptions, 'stream'>): Promise<Response>;

    upload<T = unknown>(url: string, file: File, fieldName?: string, options?: Omit<ApiClientOptions, 'data'>): Promise<T>;
}

/**
 * API 方法具体实现
 */
export const api: ApiBase = {
    get: (url, options) => apiClient(url, {...options, method: 'GET'}),

    post: (url, data, options) => apiClient(url, {...options, method: 'POST', data}),

    put: (url, data, options) => apiClient(url, {...options, method: 'PUT', data}),

    patch: (url, data, options) => apiClient(url, {...options, method: 'PATCH', data}),

    delete: (url, options) => apiClient(url, {...options, method: 'DELETE'}),

    stream: (url, options) => apiClient<Response>(url, {...options, stream: true}),

    upload: (url, file, fieldName = 'file', options) => {
        const formData = new FormData();
        formData.append(fieldName, file);

        return apiClient(url, {
            method: 'POST',
            data: formData,
            ...options,
        });
    },
};


export const getFileStream = async (
    url: string,
    params?: Record<string, string | number | boolean>
): Promise<Blob> => {
    const response = await apiClient<Response>(url, {
        headers: {
            'Content-Type': 'application/x-www-form-urlencoded',
        }, method: 'GET', params, stream: true
    });
    if (!response.ok) {
        throw new Error(`Request failed, status code: ${response.status}`);
    }
    const blob = await response.blob();
    return blob;
};
/**
 * 资源API接口
 */
// interface ResourceApi<T> {
//     getAll(params?: Record<string, string | number | boolean>): Promise<T[]>;
//     getOne(id: string | number): Promise<T>;
//     create(data: Omit<T, 'id'>): Promise<T>;
//     update(id: string | number, data: Partial<T>): Promise<T>;
//     patch(id: string | number, data: Partial<T>): Promise<T>;
//     delete(id: string | number): Promise<void>;
//     search(queryParams: Record<string, string | number | boolean>): Promise<T[]>;
// }

// /**
//  * 创建API资源方法
//  *
//  * @param resourcePath - 资源路径
//  */
// export const createResourceApi = <T = unknown>(resourcePath: string): ResourceApi<T> => {
//     return {
//         getAll: (params = {}) => api.get<T[]>(resourcePath, { params }),
//         getOne: (id) => api.get<T>(`${resourcePath}/${id}`),
//         create: (data) => api.post<T>(resourcePath, data),
//         update: (id, data) => api.put<T>(`${resourcePath}/${id}`, data),
//         patch: (id, data) => api.patch<T>(`${resourcePath}/${id}`, data),
//         delete: (id) => api.delete(`${resourcePath}/${id}`),
//         search: (queryParams) => api.get<T[]>(`${resourcePath}/search`, { params: queryParams }),
//     };
// };
